import React, { useCallback, useEffect, useState } from "react";
import { Trash2, Send, BadgeCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { PageTitle, TextInput } from "./shared";
import { CodePlate, EntryCard, fmtDate, STATUS_META } from "../../components/bloom/BloomBits";

const FILTERS = [
  { key: "", label: "ALL" },
  { key: "new", label: "FRESHLY LOGGED" },
  { key: "in_review", label: "UNDER REVIEW" },
  { key: "identified", label: "IDENTIFIED" },
];

const RecordDetail = ({ recordId, onChanged, onDeleted }) => {
  const [data, setData] = useState(null);
  const [identification, setIdentification] = useState("");
  const [reply, setReply] = useState("");
  const seededFor = React.useRef(null);

  const load = useCallback(() => {
    api.get(`/admin/bloom/${recordId}`).then((r) => {
      setData(r.data);
      if (seededFor.current !== recordId) {
        seededFor.current = recordId;
        setIdentification(r.data.record.identification || "");
      }
    }).catch(() => toast.error("Could not load this record"));
  }, [recordId]);
  useEffect(load, [load]);

  if (!data) return <p className="text-sm text-plum-deep/50 p-4">Loading…</p>;
  const { record, entries } = data;

  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/bloom/${record.id}`, { status });
      toast.success("Status updated");
      load(); onChanged();
    } catch { toast.error("Could not update status"); }
  };
  const saveIdentification = async () => {
    if (!identification.trim()) { toast.error("Type the identification first"); return; }
    try {
      await api.patch(`/admin/bloom/${record.id}`, { identification, status: "identified" });
      toast.success("Identification stamped on the passport");
      load(); onChanged();
    } catch { toast.error("Could not save identification"); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/admin/bloom/${record.id}/reply`, { notes: reply });
      setReply("");
      toast.success("Reply added to the passport timeline");
      load(); onChanged();
    } catch { toast.error("Could not send the reply"); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete passport ${record.code} and its whole history? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/bloom/${record.id}`);
      toast.success("Record deleted");
      onDeleted();
    } catch { toast.error("Could not delete the record"); }
  };

  return (
    <div className="p-4 space-y-5 bg-cream rounded-b-2xl" data-testid="bloom-admin-detail">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {["new", "in_review", "identified"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`bloom-admin-status-${s}`}
              className={`px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.16em] font-bold transition-colors ${record.status === s ? `${STATUS_META[s].bg} ${STATUS_META[s].text}` : "bg-warmwhite border border-plum-deep/15 text-plum-deep/60 hover:border-magenta"}`}
            >
              {STATUS_META[s].label}
            </button>
          ))}
        </div>
        <button onClick={remove} className="ml-auto text-plum-deep/40 hover:text-red-600" data-testid="bloom-admin-delete" aria-label="Delete record">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-plum-deep/60 uppercase tracking-[0.12em]">
        {record.contact_email && <span>EMAIL: {record.contact_email}</span>}
        {record.location && <span>LOCATION: {record.location}</span>}
        {record.planting_date && <span>PLANTED: {record.planting_date}</span>}
        <span>FIRST LOGGED: {fmtDate(record.created_at)}</span>
      </div>

      <div className="flex gap-3 items-center flex-wrap">
        <BadgeCheck className="w-5 h-5 text-forest shrink-0" />
        <TextInput
          placeholder="Team identification — e.g. Dahlia 'Cafe au Lait', confirmed from flower photos"
          value={identification}
          onChange={(e) => setIdentification(e.target.value)}
          data-testid="bloom-admin-identification"
          className="flex-1 min-w-[240px]"
        />
        <button onClick={saveIdentification} className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-2.5 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-plum" data-testid="bloom-admin-save-id">
          <Save className="w-3.5 h-3.5" /> SAVE ID
        </button>
      </div>

      <div className="flex gap-3 items-start">
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={2}
          placeholder="Reply to the grower — shows on their passport timeline"
          data-testid="bloom-admin-reply"
          className="flex-1 rounded-3xl px-5 py-3 bg-warmwhite border border-plum-deep/15 focus:outline-none focus:border-forest text-sm resize-none"
        />
        <button onClick={sendReply} className="inline-flex items-center gap-2 rounded-full bg-plum text-cream px-5 py-3 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-magenta" data-testid="bloom-admin-send-reply">
          <Send className="w-3.5 h-3.5" /> SEND
        </button>
      </div>

      <div className="space-y-3">
        {entries.map((e) => <EntryCard key={e.id} entry={e} />)}
      </div>
    </div>
  );
};

const BloomIdAdmin = () => {
  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/bloom", { params: filter ? { status: filter } : {} }).then((r) => setRecords(r.data));
  }, [filter]);
  useEffect(load, [load]);

  return (
    <div data-testid="console-bloom-id">
      <PageTitle kicker="IDENTIFICATION ENGINE" title="BLOOM ID" />
      <div className="flex gap-2 flex-wrap mb-6">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`bloom-filter-${f.key || "all"}`}
            className={`px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.16em] font-bold transition-colors ${filter === f.key ? "bg-magenta text-cream" : "bg-warmwhite border border-plum-deep/15 text-plum-deep/60 hover:border-magenta"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {records.length === 0 && <p className="text-sm text-plum-deep/50">No plant passports yet — they'll appear here when customers scan their Bloom IDs.</p>}
        {records.map((r) => (
          <div key={r.id} className="bg-warmwhite border border-plum-deep/10 rounded-2xl overflow-hidden" data-testid={`bloom-record-${r.code}`}>
            <button
              onClick={() => setOpenId(openId === r.id ? null : r.id)}
              data-testid={`bloom-record-toggle-${r.code}`}
              className="w-full flex items-center gap-4 p-4 text-left hover:bg-cream/60 transition-colors flex-wrap"
            >
              <CodePlate code={r.code} />
              <div className="flex-1 min-w-[160px]">
                <p className="font-serif-display uppercase text-plum-deep tracking-[0.08em]">{r.plant_name || "UNIDENTIFIED BLOOM"}</p>
                <p className="text-xs text-plum-deep/50">{r.entry_count} log{r.entry_count === 1 ? "" : "s"} · updated {fmtDate(r.updated_at)}</p>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.16em] font-bold rounded-full px-3 py-1.5 ${STATUS_META[r.status]?.bg} ${STATUS_META[r.status]?.text}`}>
                {STATUS_META[r.status]?.label}
              </span>
            </button>
            {openId === r.id && <RecordDetail recordId={r.id} onChanged={load} onDeleted={() => { setOpenId(null); load(); }} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BloomIdAdmin;
